/**
 * Ticket detail — Steps 18/55. Layout per Kai's reference (Freshservice):
 * a DISTINCT "Description" card (message zero — the requester's original
 * text — never inside the scrollable thread) sits above a "Conversations"
 * heading, which holds every reply/note/system line. The composer is
 * EMAIL-STYLE when replying: From (this desk's mailbox) / Reply To (the
 * requester) / a searchable Cc field, exactly like the reference's Cc "Type
 * to search". Note mode drops the email header — it's internal, not mail.
 *
 * Two approved deviations from blanket draft-first stand: status/priority/
 * urgency/impact/type/group/assignee auto-commit per change; the composer
 * autosaves as a recovery draft (never a save) — same rules as digitaladmin's
 * Hub show-plan editor (409 suspends, never overwrites the typist).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Eye,
  Lock,
  Reply,
  Mail,
  Megaphone,
  Pencil,
  RefreshCw,
  MessagesSquare,
  Send,
  SlidersHorizontal,
  UserRound,
  Ban,
  Paperclip,
  RotateCcw,
  Trash2,
  X,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useConfirm } from '../../lib/confirm';
import { FileFieldInput } from '../../features/fields/FileFieldInput';
import { FieldRenderer } from '../../features/fields/FieldRenderer';
import { AttachmentViewer, canPreview } from '../../features/attachments/AttachmentViewer';
import type { FileFieldValue } from '@itsm/shared';
import { Badge } from '../../components/ui/badge';
import { Combobox } from '../../components/ui/combobox';
import { Select } from '../../components/ui/select';
import { RichTextEditor } from '../../components/ui/richtext';
import { Skeleton } from '../../components/ui/skeleton';
import { cn } from '../../lib/utils';
import { fullTime, relativeTime } from '../../lib/time';
import { apiFetch, apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { renderReplyTemplate, firstNameOf } from '@itsm/shared';
import {
  listPathForType,
  listLabelForType,
  ticketPath,
  PRIORITY_META,
  STATUS_BADGE,
  ticketsApi,
} from '../../features/tickets/api';
import { SlaPanel } from '../../features/tickets/SlaPill';
import { EmailChipInput } from '../../features/tickets/EmailChipInput';
import { AgentChipInput } from '../../features/tickets/AgentChipInput';
import { CannedResponsePicker } from '../../features/tickets/CannedResponsePicker';
import { TicketPicker } from '../../features/tickets/TicketPicker';
import { TicketTabs, tabsForType } from '../../features/tickets/TicketTabs';
import { ActivityTab } from '../../features/tickets/ActivityTab';
import { TasksTab } from '../../features/tickets/TasksTab';
import { ApprovalsTab } from '../../features/tickets/ApprovalsTab';
import { ProblemTab } from '../../features/tickets/ProblemTab';
import { OutageTab } from '../../features/tickets/OutageTab';
import { MessageBody } from '../../features/tickets/MessageBody';
import { useTicketRealtime } from '../../features/tickets/useTicketRealtime';
import { EditTicketModal } from '../../features/tickets/EditTicketModal';
import { DeclareMajorModal } from '../../features/tickets/DeclareMajorModal';
import { ReviewTab } from '../../features/tickets/ReviewTab';
import { ChangeTab } from '../../features/tickets/ChangeTab';
import { ChangeStepper } from '../../features/tickets/ChangeStepper';
import { ChangeOutcomeCard } from '../../features/tickets/ChangeOutcomeCard';
import { ConnectionsTab } from '../../features/tickets/ConnectionsTab';
import { ResolutionTab } from '../../features/tickets/ResolutionTab';
import type { Person } from '../../features/tickets/api';
import { HeldCard, HoldModal } from './HoldControls';
import { RequestDetailsCard } from '../../features/tickets/RequestDetailsCard';
import { TicketAttachments } from '../../features/tickets/TicketAttachments';
import { Modal } from '../../components/ui/modal';
import { useDirty } from '../../lib/dirty';
import {
  IMPACT_LEVELS,
  URGENCY_LEVELS,
  TICKET_SOURCES,
  derivePriority,
  propagationLabel,
  ticketRef,
  convertibleTypes,
  canEditMessage,
  TYPE_LABEL,
} from '@itsm/shared';
import { useBranding } from '../../lib/useBranding';
import { useAuth } from '../../lib/AuthContext';
import { substitutePlaceholders } from '../../lib/placeholders';

function VRow({ label, value }: { label: string; value?: React.ReactNode }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex items-start gap-3 border-b border-border/50 py-2.5 last:border-0">
      <span className="w-24 shrink-0 pt-0.5 text-xs text-muted-foreground">{label}</span>
      <span className="min-w-0 break-words text-sm">{value}</span>
    </div>
  );
}

/**
 * Files attached to one message — Step 112.
 *
 * Reuses the Step 73 viewer rather than a second preview path: images and PDFs
 * open in the GUI with the same `Content-Disposition: attachment` and
 * scripting-disabled pdf.js guarantees, and a second implementation would be a
 * second set of those guarantees to keep right.
 */
interface MessageFile {
  id: string;
  filename: string;
  contentType: string;
  size: number;
  migrated: number;
}

function MessageAttachments({ files }: { files?: MessageFile[] | undefined }) {
  const [previewing, setPreviewing] = useState<MessageFile | null>(null);
  if (!files?.length) return null;
  const readable = (b: number) =>
    b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5 border-t pt-2">
      {files.map((f) =>
        f.migrated ? (
          <span key={f.id} className="inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs">
            <Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" />
            <span className="max-w-[16rem] truncate">{f.filename}</span>
            <span className="text-muted-foreground">{readable(f.size)}</span>
            {canPreview(f.contentType) && (
              <button
                onClick={() => setPreviewing(f)}
                className="rounded text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Preview
              </button>
            )}
            <a
              href={`/api/media/${f.id}`}
              target="_blank"
              rel="noreferrer"
              className="rounded text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Open
            </a>
          </span>
        ) : (
          // A known gap, never a broken link — the plan's rule for anything
          // the migration could not retrieve.
          <span
            key={f.id}
            className="inline-flex items-center gap-1.5 rounded-md border border-dashed px-2 py-1 text-xs text-muted-foreground"
          >
            <Paperclip className="h-3 w-3 shrink-0" />
            {f.filename} — not available
          </span>
        ),
      )}
      {previewing && (
        <Modal open onClose={() => setPreviewing(null)} title={previewing.filename}>
          <AttachmentViewer
            id={previewing.id}
            contentType={previewing.contentType}
            filename={previewing.filename}
          />
        </Modal>
      )}
    </div>
  );
}

/**
 * The visible TEXT of composer HTML, for the dirty comparison — Step 181.
 *
 * Strips tags and collapses the whitespace Tiptap rewrites on load, so a
 * pristine template compares equal to itself however the editor normalised its
 * markup. Structure-only edits (a bold, a blank line) are deliberately not
 * "dirty"; typed words are.
 */
function composerText(html: string): string {
  return String(html ?? '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function TicketDetailPage() {
  /** `tab` is undefined on `/tickets/:id` — Conversation is the default, and
   *  it is a bare path rather than `/conversation` so every existing link and
   *  bookmark still lands somewhere sensible. */
  const { id = '', tab } = useParams();
  return <TicketDetail id={id} tab={tab} />;
}

/** One component, two surfaces: the full page and (later) any embedded view. */
export function TicketDetail({
  id,
  tab,
  onBack,
}: {
  id: string;
  /** Which tab to show. `undefined` is Conversation — see the page above. */
  tab?: string | undefined;
  onBack?: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const branding = useBranding();
  const { me } = useAuth();
  /** Same bit the queue's inline editing asks for, and the same fail-closed
   *  default: without it the tabs render read-only rather than showing
   *  controls that 403 on use. */
  const canEditTickets = !!me?.permissions['tickets']?.edit;
  const location = useLocation();
  // URL state on purpose: this is the COMPOSER's mode (Reply vs Note), not a
  // view of the ticket. It is transient by design — the composer's chrome
  // changes with it — and putting it in the URL would let a shared link open
  // somebody else's composer in private-note mode, which is the one mistake
  // this control exists to prevent.
  const [mode, setMode] = useState<'public_reply' | 'private_note'>('public_reply');
  const [draft, setDraft] = useState('');
  /**
   * What the composer held when it was last set FOR the agent — a template we
   * inserted, a draft we restored, or '' after a send or discard — Step 181.
   *
   * The dirty guard compares against this rather than against empty. The
   * auto-inserted greeting is not the agent's work, so a ticket where somebody
   * opened Reply and typed nothing must not prompt "unsaved changes" on the way
   * out (Kai: *"discarding a draft doesn't seem to actually discard… as I
   * navigate away it flags unsaved changes"*). Compared as TEXT, because Tiptap
   * normalises the HTML on load and a raw-string compare would read every
   * pristine template as changed.
   */
  const composerBaselineRef = useRef('');
  /** Step 174 — the draft fetch came back empty, so a greeting is wanted once
   *  the ticket itself has loaded and we know who to greet. */
  const [noSavedDraft, setNoSavedDraft] = useState(false);
  /**
   * Step 177 — the composer is CLOSED until somebody chooses to write.
   *
   * Kai: *"it says restored your draft, the auto bit here shouldn't be there
   * unless I reply, maybe the writing bit should be collapsed until someone
   * hits reply, add note etc."*
   *
   * Both halves of that were my doing. The reply template was being inserted
   * on page LOAD, so the autosave dutifully stored a draft nobody had typed,
   * and the next visit announced it as restored work. A greeting the agent
   * never wrote is not a draft, and telling them it was rescued is worse than
   * not offering it: it teaches them the notice means nothing.
   *
   * So the template goes in when Reply or Private note is pressed, which is
   * also the first moment anyone could want it. A genuinely saved draft still
   * opens the composer by itself — that IS their work, and hiding it would be
   * the opposite mistake.
   */
  const [composerOpen, setComposerOpen] = useState(false);
  /** Bumped when the template is inserted, so the editor moves the caret into
   *  the blank line — and at no other time. */
  const [caretToken, setCaretToken] = useState<number | null>(null);
  const [cc, setCc] = useState<string[]>([]);
  const [bcc, setBcc] = useState<string[]>([]);
  const [ccOpen, setCcOpen] = useState(false);
  const [bccOpen, setBccOpen] = useState(false);
  const [notifyTo, setNotifyTo] = useState<Person[]>([]);
  const [holdModalOpen, setHoldModalOpen] = useState(false);

  /**
   * Step 124 — the properties rail is DRAFT-FIRST.
   *
   * It used to auto-commit each field on change (the plan's sanctioned
   * deviation). Kai asked for the reverse, and the argument that settles it
   * was not the one considered when the deviation was approved:
   *
   *  - changing priority + group + assignee was three writes and THREE audit
   *    rows for one human decision;
   *  - there was no Cancel — every intermediate state was committed;
   *  - once automations land (Step 32) each intermediate write is a TRIGGER,
   *    so setting priority→urgent then group→Infra could fire rules the agent
   *    never intended.
   *
   * `props` holds only what has been CHANGED, so a field nobody touched is
   * absent from the PATCH — which is what stops a stale value overwriting a
   * concurrent edit by someone else.
   */
  const [props, setProps] = useState<Record<string, unknown>>({});

  // ── Composer autosave — the plan's one approved exception, and its two
  // non-negotiable rules: a 409 SUSPENDS rather than retries (never fights a
  // second tab for the last write), and it never overwrites what's on screen
  // — a restored draft only ever loads once, on arrival, never mid-typing.
  const [draftUpdatedAt, setDraftUpdatedAt] = useState<number | null>(null);
  const [draftSuspended, setDraftSuspended] = useState(false);
  const [restoredAt, setRestoredAt] = useState<number | null>(null);
  const [saveIndicator, setSaveIndicator] = useState<'idle' | 'saving' | 'saved'>('idle');
  const draftLoadedFor = useRef<string | null>(null);
  const savingDraftRef = useRef(false);
  const saveDraftAgainRef = useRef(false);

  useEffect(() => {
    if (draftLoadedFor.current === id) return;
    draftLoadedFor.current = id;
    composerBaselineRef.current = '';
    setComposerOpen(false);
    setDraftUpdatedAt(null);
    setDraftSuspended(false);
    setRestoredAt(null);
    setSaveIndicator('idle');
    ticketsApi.draft(id).then((res) => {
      const d = res.draft;
      /**
       * Step 174 — open with a greeting, so you can start typing.
       *
       * Kai: *"no automatic Hi {First Name}... to be able to start typing
       * instantly."* Every reply begins the same way, and making somebody type
       * it forty times a day is forty small frictions between them and the
       * answer.
       *
       * Only when there is NO saved draft. A restored draft is the agent's own
       * unsent words and prefixing a greeting to it would corrupt something
       * they wrote — the one thing the draft machinery exists to protect.
       */
      if (!d) return setNoSavedDraft(true);
      const hasContent = d.bodyHtml.replace(/<[^>]+>/g, '').trim().length > 0;
      /**
       * An emptied draft row is NOT a draft — Step 179.
       *
       * This used to fall out of the effect without setting `noSavedDraft`,
       * and the effect is what tells Reply whether it may insert the template.
       * So clearing the composer by hand — which is what anybody does when
       * they want to start again — left a row behind that silently turned the
       * greeting off for that ticket, for good. Nothing on screen connected
       * the two, and pressing Reply just gave you an empty box.
       */
      if (!hasContent && !d.cc.length && !d.bcc.length && !d.mentions.length) return setNoSavedDraft(true);
      setMode(d.kind);
      // A restored draft is already saved on the server, so it is the
      // baseline: leaving it loses nothing, and only further typing is dirty.
      composerBaselineRef.current = d.bodyHtml;
      setDraft(d.bodyHtml);
      setCc(d.cc);
      setBcc(d.bcc);
      if (d.cc.length) setCcOpen(true);
      if (d.bcc.length) setBccOpen(true);
      setNotifyTo(d.mentions);
      setDraftUpdatedAt(d.updatedAt);
      setRestoredAt(d.updatedAt);
      setComposerOpen(true); // their own unsent words — show them
    });
  }, [id]);

  async function saveDraft() {
    if (savingDraftRef.current) {
      saveDraftAgainRef.current = true;
      return;
    }
    savingDraftRef.current = true;
    setSaveIndicator('saving');
    const res = await ticketsApi.saveDraft(id, {
      kind: mode,
      bodyHtml: draft,
      ...(cc.length ? { cc } : {}),
      ...(bcc.length ? { bcc } : {}),
      ...(notifyTo.length ? { mentions: notifyTo.map((p) => p.id) } : {}),
      expectedUpdatedAt: draftUpdatedAt,
    });
    savingDraftRef.current = false;
    const queued = saveDraftAgainRef.current;
    saveDraftAgainRef.current = false;
    if (res.status === 409) {
      setDraftSuspended(true);
      setSaveIndicator('idle');
      return;
    }
    if (res.ok && res.body) setDraftUpdatedAt(res.body.updatedAt);
    // Typed again while the request was in flight — go again with the token
    // the server just handed back, rather than dropping that keystroke.
    if (queued) {
      void saveDraft();
    } else {
      setSaveIndicator('saved');
    }
  }

  useEffect(() => {
    const hasContent = draft.replace(/<[^>]+>/g, '').trim().length > 0;
    if (draftSuspended || (!hasContent && draftUpdatedAt === null)) return;
    const t = setTimeout(() => void saveDraft(), 1500);
    return () => clearTimeout(t);
  }, [mode, draft, cc, bcc, notifyTo, draftSuspended]);

  // Step 60 — presence and invalidation for this ticket.
  const { alsoViewing, live, changedBy, dismissChange } = useTicketRealtime(id, me?.user.id);
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: qk.tickets.detail(id),
    queryFn: () => ticketsApi.detail(id),
    /**
     * Step 60 — poll ONLY when the socket is down.
     *
     * The plan's stated fallback. `false` while live, because a socket that
     * already invalidates on every write plus a 30s poll is the poll doing
     * nothing but load.
     */
    refetchInterval: live ? false : 30_000,
  });
  /**
   * Put the URL right once the type is known. Kai: *"the url should be
   * /problems or /changes etc"*.
   *
   * Done HERE rather than at every link: the app has dozens of
   * `navigate('/tickets/<id>')` calls, and there are already links out in
   * emails that nobody can go back and change. `replace` so it does not add a
   * history entry — Back should leave the ticket, not bounce between two
   * spellings of the same address.
   */
  useEffect(() => {
    const type = data?.ticket?.type;
    if (!type) return;
    /**
     * A tab this TYPE does not have falls back to Details.
     *
     * The type can change under your feet: demoting a major incident from the
     * Outage tab left you standing on `/outage`, which an incident does not
     * have, and the page went blank (Kai). Handled here rather than at that
     * one button because every conversion has the same shape, and a
     * hand-typed `/tickets/x/review` had it already.
     */
    const allowed = tabsForType(type).some((t) => t.slug === (tab ?? ''));
    const want = ticketPath(type, id, allowed ? tab : undefined);
    if (location.pathname !== want) navigate(want, { replace: true });
  }, [data?.ticket?.type, id, tab, location.pathname, navigate]);

  /**
   * The greeting, once both answers are in.
   *
   * An effect rather than a line inside the draft fetch, because those two
   * requests race: the draft can come back before the ticket, and then there
   * is no requester name to greet and the composer just stays blank. This
   * waits for whichever is slower.
   *
   * First name only, because that is how a person writes — the full name
   * reads like a form letter. Nothing at all when the requester has no name on
   * record: "Hi ," is worse than starting blank. And never over something
   * already typed.
   */
  const myReply = useQuery({
    queryKey: qk.mySignature,
    queryFn: () =>
      apiFetch<{
        generated: string;
        signature: string | null;
        /** The DESK's template — Step 177 moved it out of personal settings. */
        replyTemplate: string;
      }>('/api/org/me/signature'),
    staleTime: 10 * 60_000,
  });

  /** Open the composer in a mode, putting the template in if it is empty. */
  function openComposer(next: 'public_reply' | 'private_note') {
    setMode(next);
    setComposerOpen(true);
    // A private note is internal: no greeting, no signature, nobody is being
    // written to. The template is for replies.
    if (next === 'private_note' || !noSavedDraft || !data || !myReply.data) return;
    insertTemplate();
  }

  function insertTemplate() {
    if (!data || !myReply.data) return;
    const t = data.ticket;
    setNoSavedDraft(false); // once per ticket, whatever happens next
    const opening = renderReplyTemplate(myReply.data.replyTemplate, {
      firstName: firstNameOf(t.requester?.name),
      fullName: t.requester?.name ?? null,
      ref: ticketRef(t.type, t.number),
      // The rendered signature, so what the agent sees in the composer is what
      // the requester will receive — this composer's HTML is the message body,
      // nothing appends to it later. Their own saved signature wins; the
      // generated one is what makes this work on somebody's first day.
      signatureHtml: myReply.data.signature || myReply.data.generated || null,
    });
    setDraft((current) => {
      if (composerText(current)) return current; // real content already there
      composerBaselineRef.current = opening; // the greeting is not the agent's work
      return opening;
    });
    setCaretToken(Date.now());
  }

  const { data: peopleData } = useQuery({ queryKey: qk.people, queryFn: ticketsApi.people });
  const { data: groupsData } = useQuery({
    queryKey: qk.orgGroups,
    queryFn: ticketsApi.groups,
    staleTime: 5 * 60_000,
  });

  /**
   * What the draft actually CHANGES — not merely which keys it has touched.
   *
   * `props` records every key the agent has interacted with, so picking
   * "High" and then picking "Medium" again left `priority` sitting in it
   * looking like an edit: Update stayed enabled, the panel claimed "1 unsaved
   * change", and saving wrote a value identical to the stored one — which the
   * audit log then records as a change (Kai, 2026-09-06: "changing an option,
   * then changing it back records this as a change").
   *
   * An audit trail that reports edits nobody made is worse than no trail: it
   * is the record people reach for when they are trying to work out who did
   * what, and noise there costs more than it saves.
   *
   * So the draft is DIFFED against the saved row, once, and the result drives
   * all three things that were reading the raw object — whether Update is
   * enabled, the count shown beside it, and what actually gets sent.
   */
  const changedProps = useMemo(() => {
    if (!data?.ticket) return {} as Record<string, unknown>;
    const row = data.ticket as unknown as Record<string, unknown> & {
      group: { id: string } | null;
    };
    const saved = (key: string): unknown => {
      // The one key whose stored value is not a plain column: the group
      // arrives nested, and comparing against `undefined` would make every
      // group choice look like a change.
      if (key === 'groupId') return row.group?.id ?? null;
      return row[key];
    };
    // '' comes from a cleared <select>, undefined from an absent column, and
    // both MEAN null. Without this, clearing an already-empty field reads as
    // an edit.
    const norm = (v: unknown) => (v === '' || v === undefined ? null : v);
    return Object.fromEntries(Object.entries(props).filter(([k, v]) => !Object.is(norm(v), norm(saved(k)))));
  }, [props, data]);
  const changedCount = Object.keys(changedProps).length;
  const propsDirty = changedCount > 0;
  // Global Standard: a half-written message is unsaved work; leaving must ask.
  // Step 124: unsaved PROPERTY changes are dirty too, not just the composer.
  /** Draft answers for CUSTOM ticket fields — saved by the same Update button
   *  as the built-in properties, through the fields PATCH. */
  const [fieldsDraft, setFieldsDraft] = useState<Record<string, unknown>>({});
  const fieldsDirty = Object.keys(fieldsDraft).length > 0;
  useDirty(composerText(draft) !== composerText(composerBaselineRef.current) || propsDirty || fieldsDirty);

  /** Step 44 — watching is an IMMEDIATE act, like Pick up, not a drafted
   *  property: you either want the emails or you do not, and making that wait
   *  for Update would mean missing the very change you wanted to hear about. */
  const [watchError, setWatchError] = useState<string | null>(null);
  const watch = useMutation({
    mutationFn: (userId?: string) => ticketsApi.watch(id, userId),
    // A refusal has to be VISIBLE. The first cut showed nothing at all, so a
    // 404 (platform users were excluded by a too-narrow `kind` check) read as
    // "the button does nothing" — Kai's exact words, and a fair description.
    onSuccess: (r: any) => {
      setWatchError(r?.ok === false ? (r.error ?? 'That did not work.') : null);
      void queryClient.invalidateQueries({ queryKey: qk.tickets.detail(id) });
    },
    onError: (e: any) => setWatchError(String(e?.message ?? 'That did not work.')),
  });
  const unwatch = useMutation({
    mutationFn: (userId: string) => ticketsApi.unwatch(id, userId),
    onSuccess: (r: any) => {
      setWatchError(r?.ok === false ? (r.error ?? 'That did not work.') : null);
      void queryClient.invalidateQueries({ queryKey: qk.tickets.detail(id) });
    },
    onError: (e: any) => setWatchError(String(e?.message ?? 'That did not work.')),
  });

  /**
   * Step 54 — the outage record, read here so the banner can offer End as
   * well as show the declaration. Only fetched for a major incident: every
   * other ticket would be asking a question with no answer.
   */
  const mi = useQuery({
    queryKey: qk.tickets.majorIncident(id),
    queryFn: () =>
      apiFetch<{ entry: { declaredAt: number | null; stoodDownAt: number | null } | null }>(
        `/api/tickets/${id}/major-incident`,
      ),
    enabled: !!id && data?.ticket.type === 'major_incident',
  });
  const standDown = useMutation({
    mutationFn: (stoodDownAt: number | null) =>
      apiSend(`/api/tickets/${id}/major-incident`, 'PUT', { stoodDownAt }),
    onSuccess: (r) => {
      if (!r.ok) return toast.error(r.error ?? 'Could not save.');
      void queryClient.invalidateQueries({ queryKey: qk.tickets.majorIncident(id) });
      // The review's publish gate reads the stand-down.
      void queryClient.invalidateQueries({ queryKey: qk.tickets.pir(id) });
      void queryClient.invalidateQueries({ queryKey: qk.tickets.activity(id) });
    },
  });

  /** Step 149 — custom ticket fields save through the fields PATCH. */
  const patchFields = useMutation({
    mutationFn: (values: Record<string, unknown>) => ticketsApi.patchFields(id, values),
    onSuccess: (res) => {
      if (!res.ok) {
        toast.error(res.error ?? 'Could not save those fields.');
        return;
      }
      void queryClient.invalidateQueries({ queryKey: qk.tickets.detail(id) });
    },
  });

  /**
   * Step 171 — make changes without emailing anybody.
   *
   * Kai: *"there needs to be a resolve or close without sending email
   * function, in bulk or 1 by 1."* Bulk already worked, because the bulk
   * endpoint never notified; this is the one-by-one half.
   *
   * A sticky switch rather than a per-action prompt. The cases where you want
   * it come in runs — a duplicate, then the four others like it, then the
   * mailbox backlog — and a dialog per ticket would be its own punishment. It
   * resets when you leave the ticket, so it can never be quietly on for the
   * next person's work.
   *
   * It suppresses notifications only. The audit log still records everything;
   * silent to the customer is not the same as unrecorded.
   */
  const [silent, setSilent] = useState(false);

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      ticketsApi.patch(id, silent ? { ...body, silent: true } : body),
    onSuccess: (res) => {
      /**
       * Step 127 — a resolution is required to resolve or close, and the API
       * says so with a machine-readable `code`.
       *
       * Prompting IN PLACE, not navigating to the tab.
       *
       * Navigating was the first attempt and it deadlocked: the pending
       * status change is itself an unsaved draft, so the router guard caught
       * the navigation and put a Keep/Discard dialog in front of the very
       * editor the agent was being sent to. Proven in the browser — the
       * dialog appears and the URL never changes.
       *
       * A modal has none of that problem, keeps the draft intact, and is what
       * "prompted at the status change" meant in the first place. The rule
       * stays enforced on the server; this is the courtesy layer over it.
       */
      if (!res.ok) {
        if ((res.errorBody as { code?: string } | undefined)?.code === 'resolution_required') {
          setResolutionPrompt(true);
          return;
        }
        toast.error(res.error ?? 'Could not save that change.');
        return;
      }
      void queryClient.invalidateQueries({ queryKey: qk.tickets.detail(id) });
      void queryClient.invalidateQueries({ queryKey: qk.tickets.all });
      void queryClient.invalidateQueries({ queryKey: qk.tickets.activity(id) });
    },
  });

  /** Step 127 — the resolution prompt, raised by the API refusing to
   *  resolve without one. */
  const [resolutionPrompt, setResolutionPrompt] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  /** Step 111 — trash / spam / restore, immediate and audited. Not draft-first
   *  on purpose: this is an atomic act on one ticket, the same class as the
   *  status picker, and holding it behind a Save would be a dialog about a
   *  dialog. */
  const confirm = useConfirm();
  /**
   * Throw the draft away — Step 179.
   *
   * Kai: *"I can't discard messages in the UI, because they're auto saved they
   * also don't by themselves."* Both halves were true. Closing the composer
   * keeps the draft on purpose, send clears it, and there was no third door —
   * so an agent who started a reply and changed their mind had no way to say
   * so, and the row sat there re-opening the composer on every visit.
   *
   * Confirmed because it destroys writing, and confirming is cheap next to
   * losing six paragraphs. `noSavedDraft` goes back on so the next Reply opens
   * with the greeting again: discarding means starting over, not starting
   * blank.
   */
  async function discardDraft() {
    const wrote = draft.replace(/<[^>]+>/g, '').trim().length > 0;
    if (
      wrote &&
      !(await confirm({
        title: 'Discard this draft?',
        body: 'What you have written here is deleted and cannot be brought back.',
        confirmLabel: 'Discard',
        destructive: true,
      }))
    )
      return;
    await ticketsApi.discardDraft(id);
    composerBaselineRef.current = '';
    setDraft('');
    setCc([]);
    setBcc([]);
    setCcOpen(false);
    setBccOpen(false);
    setNotifyTo([]);
    setComposerFiles([]);
    setDraftUpdatedAt(null);
    setDraftSuspended(false);
    setRestoredAt(null);
    setSaveIndicator('idle');
    setNoSavedDraft(true);
    setComposerOpen(false);
  }

  /** Step 112 — attachments on THIS reply, distinct from the ticket's own
   *  file drawer. Uploaded as `pending` and claimed onto the message on send,
   *  so abandoning a draft leaks nothing. */
  const [composerFiles, setComposerFiles] = useState<FileFieldValue[]>([]);
  /**
   * Step 154 — copy this message onto every child and linked incident.
   *
   * Held here rather than in the composer so it resets with everything else on
   * send. Defaulted off and RE-defaulted off after every send: a sticky
   * "also post to 40 tickets" is how the second reply goes somewhere nobody
   * intended.
   */
  const [propagate, setPropagate] = useState(false);
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  async function lifecycle(action: 'trash' | 'spam' | 'restore') {
    setLifecycleBusy(true);
    const res = await apiSend(`/api/tickets/${id}/lifecycle`, 'POST', { action });
    setLifecycleBusy(false);
    if (!res.ok) return toast.error(res.error ?? 'That could not be done.');
    toast.success(
      action === 'restore'
        ? 'Restored to the queue.'
        : action === 'spam'
          ? 'Marked as spam.'
          : 'Moved to the recycle bin.',
    );
    void queryClient.invalidateQueries({ queryKey: qk.tickets.detail(id) });
    void queryClient.invalidateQueries({ queryKey: qk.tickets.all });
  }
  const [declareOpen, setDeclareOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const mergeInto = useMutation({
    mutationFn: (intoId: string) => ticketsApi.merge(id, intoId),
    onSuccess: (res) => {
      if (res.ok && res.body) {
        setMergeOpen(false);
        navigate(`/tickets/${res.body.mergedInto}`, { replace: true });
      }
    },
  });
  const linkTicket = useMutation({
    mutationFn: (linkedId: string) => ticketsApi.link(id, linkedId),
    onSuccess: (res) => {
      if (res.ok) {
        setLinkOpen(false);
        void queryClient.invalidateQueries({ queryKey: qk.tickets.detail(id) });
      }
    },
  });
  const unlinkTicket = useMutation({
    mutationFn: (linkedId: string) => ticketsApi.unlink(id, linkedId),
    onSuccess: (res) => {
      if (res.ok) void queryClient.invalidateQueries({ queryKey: qk.tickets.detail(id) });
    },
  });

  /**
   * Step 154 — what a reply here would ALSO reach.
   *
   * Asked of the server rather than counted from `data.links` in the browser:
   * the two are not the same set (`links` is the symmetric, untyped Step 21
   * relation, which deliberately does NOT propagate), and a checkbox that
   * promises a different number from the one the server acts on is worse than
   * no checkbox at all.
   */
  /** Step 149 — the org's properties layout; one fetch, cached across tickets. */
  const propertiesLayout = useQuery({
    queryKey: qk.tickets.propertiesLayout,
    queryFn: () => ticketsApi.propertiesLayout(),
    staleTime: 60_000,
  });
  const layoutFields = propertiesLayout.data?.fields ?? [];
  /**
   * Source options come from the `source` definition — the admin's list, not
   * the bundle's. Machine-set channels are never offered to a human (a person
   * claiming `monitoring` puts a lie in the alert pipeline's reports), and an
   * archived option is shown only if this ticket already holds it.
   */
  const sourceDef = layoutFields.find(
    (f) => f.definition.isSystem && f.definition.key === 'source',
  )?.definition;
  const MACHINE_SOURCES = ['monitoring', 'api'];
  const currentSource = data?.ticket?.source;
  const sourceOptions = (
    sourceDef?.config.options?.length
      ? sourceDef.config.options
      : TICKET_SOURCES.map((x) => ({ value: x.key, label: x.label }))
  )
    .filter((o) => !MACHINE_SOURCES.includes(o.value) || o.value === currentSource)
    .filter((o) => !('archived' in o && o.archived) || o.value === currentSource);

  const downstream = useQuery({
    queryKey: qk.tickets.downstream(id),
    queryFn: () => ticketsApi.downstream(id),
  });
  const downstreamCount = downstream.data?.total ?? 0;

  const sendMessage = useMutation({
    mutationFn: () =>
      ticketsApi.addMessage(id, {
        kind: mode,
        bodyHtml: draft,
        ...(mode === 'public_reply' && cc.length ? { cc } : {}),
        ...(mode === 'public_reply' && bcc.length ? { bcc } : {}),
        ...(mode === 'private_note' && notifyTo.length ? { mentions: notifyTo.map((p) => p.id) } : {}),
        // Step 112 — files said WITH this message.
        ...(composerFiles.length ? { attachmentIds: composerFiles.map((f) => f.id) } : {}),
        // Step 154 — and onto everything downstream, if they asked.
        ...(propagate ? { propagate: true } : {}),
      }),
    onSuccess: (res) => {
      if (res.ok) {
        // Said out loud, because it is the one thing about this send the
        // thread will not show: the copies land on OTHER tickets.
        const n = res.body?.propagated ?? 0;
        if (n > 0) toast.success(`Copied to ${n} linked ticket${n === 1 ? '' : 's'}.`);
        composerBaselineRef.current = '';
        setDraft('');
        setComposerFiles([]);
        setPropagate(false);
        setCc([]);
        setBcc([]);
        setCcOpen(false);
        setBccOpen(false);
        setNotifyTo([]);
        setDraftUpdatedAt(null);
        setDraftSuspended(false);
        setRestoredAt(null);
        setSaveIndicator('idle');
        /**
         * Close it, and say so — Step 207.
         *
         * Kai: *"hitting send on a private note didn't automatically close it,
         * so it didn't look sent."* Every other piece of composer state was
         * reset here and this one was not, so the box stayed open and empty.
         * An empty composer is what you get from discarding a draft as well as
         * from sending one, and the two are indistinguishable at a glance.
         *
         * A note is the worst case: a public reply at least sends an email
         * somebody can see leaving, while a note's only evidence is a new row
         * in a thread that is still refetching. So the confirmation is said
         * out loud rather than left to the thread arriving — and it names
         * which KIND was sent, because posting a note where you meant a reply
         * (or worse, the reverse) is the mistake this composer exists to make
         * hard.
         */
        setComposerOpen(false);
        toast.success(mode === 'private_note' ? 'Note added — only agents see it.' : 'Reply sent.');
        void ticketsApi.discardDraft(id);
        void queryClient.invalidateQueries({ queryKey: qk.tickets.detail(id) });
        void queryClient.invalidateQueries({ queryKey: qk.tickets.all });
      }
    },
  });

  if (isPending) {
    return (
      <div className="space-y-3 p-6">
        <Skeleton className="h-6 w-96" />
        <Skeleton className="h-32 w-full max-w-2xl" />
      </div>
    );
  }
  if (isError)
    return (
      <div className="m-6 max-w-lg space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
        <p className="text-sm font-medium text-destructive">Could not load this ticket</p>
        <p className="text-xs text-destructive/80">{(error as Error).message}</p>
        <button
          onClick={() => refetch()}
          className="text-xs text-destructive/80 underline hover:text-destructive"
        >
          Retry
        </button>
      </div>
    );
  if (!data) return null;
  if (data.mergedInto) {
    navigate(`/tickets/${data.mergedInto}`, { replace: true });
    return null;
  }

  const t = data.ticket;
  const allAgents = (peopleData?.entries ?? []).filter((p) => p.kind === 'agent' || p.kind === 'platform');
  const serverGroup = (t as unknown as { group: { id: string } | null }).group;
  /**
   * The group the assignee picker must obey is the DRAFTED one, not the saved
   * one — Kai: *"when I change the group, before a save, I can't also choose
   * the agent"*.
   *
   * A regression from Step 124: the picker was still reading the server's
   * group, so after drafting a new group it offered the OLD group's members
   * (or nothing, if the ticket had no group yet) and refused to open. Draft
   * and saved state have to agree about what the ticket currently is, and the
   * draft is what the agent can see.
   *
   * `props.groupId` may legitimately be null (drafting "no group"), so the
   * `in` check is what distinguishes "drafted to none" from "not drafted".
   */
  const effectiveGroupId = 'groupId' in props ? (props.groupId as string | null) : (serverGroup?.id ?? null);
  const currentGroup = effectiveGroupId
    ? ((groupsData?.entries ?? []).find((g) => g.id === effectiveGroupId) ?? serverGroup)
    : null;
  const groupMemberIds = effectiveGroupId
    ? (groupsData?.entries ?? []).find((g) => g.id === effectiveGroupId)?.memberIds
    : undefined;
  const agents = groupMemberIds ? allAgents.filter((a) => groupMemberIds.includes(a.id)) : [];
  const assigneeOutsideGroup =
    !!t.assigneeId && (!groupMemberIds || !groupMemberIds.includes(t.assigneeId))
      ? allAgents.find((a) => a.id === t.assigneeId)
      : undefined;
  /** The picker-upper's own group, used only when the ticket has none — the
   *  API will not accept an assignee without one. */
  const myFirstGroupId =
    (groupsData?.entries ?? []).find((g) => me && g.memberIds?.includes(me.user.id))?.id ?? null;
  const isNote = mode === 'private_note';

  // Message zero is the requester's original text — the Description, ALWAYS
  // pinned above the thread, never scrolled away with it (Kai's reference).
  const [description, ...conversation] = data.messages;
  /** A catalogue request's "description" is generated from its answers, which
   *  Request Details already renders properly. */
  const fromCatalogue = !!data.catalogueItem && (data.requestFields?.length ?? 0) > 0;
  const iAmWatching = !!me && (data.watchers ?? []).some((w) => w.userId === me.user.id);

  /**
   * The priority matrix, previewed in the DRAFT.
   *
   * Urgency × impact drives priority, and the server has always done that on
   * write. It used to be visible because urgency and impact auto-committed:
   * you picked one, it saved, the answer came back. Step 124 moved them into
   * the draft, and the derivation stayed server-side — so the panel went on
   * showing the OLD priority until you pressed Update (Kai: "priority doesn't
   * get auto set now by the matrix before save").
   *
   * Worse than cosmetic: an agent looking at a stale value can "correct" it
   * by picking that same value, which the server then reads as an explicit
   * override and the matrix loses to a click nobody meant as one.
   *
   * So the rule below MIRRORS `tickets-api`'s exactly — an explicit priority
   * in this draft wins; otherwise, if this draft touches urgency or impact
   * and both end up set, the matrix decides. Two implementations of one rule
   * is a real risk, and Step 105 (the configurable matrix) is where it
   * collapses back to one.
   */
  const draftUrgency = (
    props.urgency !== undefined ? props.urgency : (t as unknown as { urgency: string | null }).urgency
  ) as string | null;
  const draftImpact = (
    props.impact !== undefined ? props.impact : (t as unknown as { impact: string | null }).impact
  ) as string | null;
  const matrixDrives =
    (props.urgency !== undefined || props.impact !== undefined) && !!draftUrgency && !!draftImpact;
  const shownPriority =
    props.priority !== undefined
      ? (props.priority as string)
      : matrixDrives
        ? derivePriority(draftUrgency as never, draftImpact as never)
        : t.priority;

  /**
   * Step 149 — the eight built-in properties, each a render function the
   * layout calls by key.
   *
   * Kai, 2026-09-06: *"Everything should be editable, nothing hard coded."*
   * The controls stay specific — a status is a status and keeps its hold
   * rule — but WHICH rows appear, in what ORDER, under what LABEL and (for
   * Source) with what OPTIONS now come from the org's "Ticket properties"
   * layout. Custom ticket fields sit among them through the generic renderer.
   * An org with no layout yet gets the built-in eight in the default order,
   * which is exactly what the rail was before.
   */
  const systemRows: Record<string, (label: string) => React.ReactNode> = {
    status: (label: string) => (
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">{label}</label>
        {/* A plain select, not a searchable one. Kai's rule: only Group
                  and Assignee are worth searching — they are the only lists
                  that grow with the organisation. Everything else here is
                  under five fixed options, where a search box is a click and a
                  popover in front of a choice you could already see. */}
        <Select
          value={(props.status as string) ?? t.status}
          onChange={(e) => {
            const v = e.target.value;
            // A hold needs a REASON, so it keeps its modal and commits
            // immediately — it is not a property you can leave in a draft.
            if (v === 'on_hold') setHoldModalOpen(true);
            else setProps((p) => ({ ...p, status: v }));
          }}
        >
          {data.statuses.map((s) => (
            <option key={s.key} value={s.key}>
              {s.name}
            </option>
          ))}
        </Select>
      </div>
    ),
    urgency: (label: string) => (
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">{label}</label>
        <Select
          value={(props.urgency as string) ?? (t as unknown as { urgency: string | null }).urgency ?? ''}
          onChange={(e) => setProps((p) => ({ ...p, urgency: e.target.value || null }))}
        >
          {/* Empty is a real value here — urgency is nullable, and the
                      dash has to be choosable, not just a placeholder. */}
          <option value="">—</option>
          {URGENCY_LEVELS.map((u) => (
            <option key={u} value={u}>
              {u[0]!.toUpperCase() + u.slice(1)}
            </option>
          ))}
        </Select>
      </div>
    ),
    impact: (label: string) => (
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">{label}</label>
        <Select
          value={(props.impact as string) ?? (t as unknown as { impact: string | null }).impact ?? ''}
          onChange={(e) => setProps((p) => ({ ...p, impact: e.target.value || null }))}
        >
          <option value="">—</option>
          {IMPACT_LEVELS.map((i) => (
            <option key={i} value={i}>
              {i[0]!.toUpperCase() + i.slice(1)}
            </option>
          ))}
        </Select>
      </div>
    ),
    priority: (label: string) => (
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">
          {label}
          {draftUrgency && draftImpact && (
            <span className="ml-1 font-normal text-muted-foreground/70">
              · from urgency × impact; editing overrides
            </span>
          )}
        </label>
        <Select value={shownPriority} onChange={(e) => setProps((p) => ({ ...p, priority: e.target.value }))}>
          {Object.entries(PRIORITY_META).map(([key, meta]) => (
            <option key={key} value={key}>
              {meta.label}
            </option>
          ))}
        </Select>
      </div>
    ),
    type: (label: string) => (
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">{label}</label>
        {/**
         * Built from `convertibleTypes`, not two hardcoded options.
         *
         * Kai: *"why is a change type an incident"*. With only
         * incident and service_request listed, a change or a problem
         * matched NEITHER — so the browser fell through to option
         * zero and rendered "Incident". Worse than wrong on screen:
         * picking it would have converted the ticket and orphaned its
         * plan. The list always contains the current type, which is
         * what makes that impossible by construction.
         */}
        <Select
          value={(props.type as string) ?? t.type}
          disabled={convertibleTypes(t.type).length < 2}
          onChange={(e) => setProps((p) => ({ ...p, type: e.target.value }))}
        >
          {convertibleTypes(t.type).map((ty) => (
            <option key={ty} value={ty}>
              {TYPE_LABEL[ty] ?? ty}
            </option>
          ))}
        </Select>
        <p className="mt-1 text-[11px] text-muted-foreground">
          {convertibleTypes(t.type).length < 2
            ? `A ${(TYPE_LABEL[t.type] ?? t.type).toLowerCase()} keeps its type — it has its own plan and lifecycle.`
            : 'Converting keeps the number, thread and every field.'}
        </p>
      </div>
    ),
    group: (label: string) => (
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">{label}</label>
        <Combobox
          value={(props.groupId as string) ?? currentGroup?.id ?? null}
          options={(groupsData?.entries ?? []).map((g) => ({ value: g.id, label: g.name }))}
          onChange={(v) =>
            setProps((p) => {
              const next: Record<string, unknown> = { ...p, groupId: v };
              // Stranding an assignee outside their group is refused by
              // the API, so the draft must not let you build that state.
              const members = (groupsData?.entries ?? []).find((g) => g.id === v)?.memberIds;
              const assignee = ('assigneeId' in p ? p.assigneeId : t.assigneeId) as string | null;
              if (assignee && (!members || !members.includes(assignee))) next.assigneeId = null;
              return next;
            })
          }
          placeholder="No group"
        />
      </div>
    ),
    assignee: (label: string) => (
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">{label}</label>
        <Combobox
          value={(props.assigneeId as string) ?? t.assigneeId}
          disabled={!currentGroup}
          options={[
            ...(assigneeOutsideGroup
              ? [
                  {
                    value: assigneeOutsideGroup.id,
                    label: `${assigneeOutsideGroup.name ?? assigneeOutsideGroup.email} (not in this group)`,
                  },
                ]
              : []),
            ...agents.map((a) => ({ value: a.id, label: a.name ?? a.email })),
          ]}
          onChange={(v) => setProps((p) => ({ ...p, assigneeId: v }))}
          placeholder="Unassigned"
        />
        <div className="mt-1 flex items-baseline justify-between gap-2">
          <p className="text-[11px] text-muted-foreground">
            {currentGroup
              ? `Members of ${(groupsData?.entries ?? []).find((g) => g.id === currentGroup.id)?.name ?? 'the group'}.`
              : 'Pick a group first — agents are chosen from the group.'}
          </p>
          {/* Step 130 — the agent half of the person page. Beside the
                    picker, not instead of it: reassigning is the common act,
                    looking someone up is the occasional one. */}
          {t.assigneeId && (
            <Link
              to={`/people/${t.assigneeId}`}
              className="shrink-0 rounded text-[11px] text-brand underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Profile
            </Link>
          )}
        </div>
      </div>
    ),
    source: (label: string) => (
      <div>
        <p className="mb-1 text-xs font-medium text-muted-foreground">{label}</p>
        <Select
          value={(props.source as string) ?? t.source}
          onChange={(e) => setProps((p) => ({ ...p, source: e.target.value }))}
          className="h-8 text-xs"
        >
          {sourceOptions.map((src) => (
            <option key={src.value} value={src.value}>
              {src.label}
            </option>
          ))}
        </Select>
      </div>
    ),
  };
  const DEFAULT_ORDER = ['status', 'urgency', 'impact', 'priority', 'type', 'group', 'assignee', 'source'];
  // The ticket's saved JSON answers, typed once so the spread below is over a
  // known object whatever the wire type says.
  const storedFields: Record<string, unknown> =
    (t.fields as Record<string, unknown> | null | undefined) ?? {};
  const propertyRows: React.ReactNode[] = layoutFields.length
    ? layoutFields.map((f) =>
        f.definition.isSystem ? (
          <div key={f.fieldId}>{systemRows[f.definition.key]?.(f.definition.label) ?? null}</div>
        ) : (
          <div key={f.fieldId}>
            <FieldRenderer
              fields={[f]}
              values={{ ...storedFields, ...fieldsDraft }}
              onChange={(key, value) => setFieldsDraft((d) => ({ ...d, [key]: value }))}
              surface="agent"
            />
          </div>
        ),
      )
    : DEFAULT_ORDER.map((k) => <div key={k}>{systemRows[k]!(k[0]!.toUpperCase() + k.slice(1))}</div>);

  return (
    // Full bleed on purpose: thread and rail scroll independently, each padded itself.
    <div className="flex min-h-0 flex-col lg:h-full lg:flex-row">
      {/* Step 141 — one page, two layouts. Below lg the thread and the
          properties rail STACK and the shell's main column scrolls the whole
          page; from lg they sit side by side and each scrolls itself, as
          before. The same components, so a phone is not a lesser version of
          the page. */}
      {/* The scroller is the THREAD, not the column.
          It was `overflow-y-auto` on the column with a `flex-1 min-h-0` body
          inside it, and that combination silently draws the conversation ON
          TOP of the composer: `flex-1` means flex-basis 0, so the body is
          given whatever height is left over, its real content is taller, and
          with no overflow of its own it spills out and paints over the next
          sibling. The column's own scrollbar never appears, because flex had
          already "fitted" everything by shrinking the body.
          So: the column clips, the thread scrolls, and the header and the
          composer are docked — which is what they were meant to be.
          `min-w-0` for the same reason on the other axis: a flex item's
          default `min-width: auto` refuses to shrink below its content, so a
          wide form or one long unbroken string pushes the properties rail off
          the right edge instead of scrolling inside itself. TicketsPage
          already had this; the detail page never did. */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:overflow-hidden">
        {/* Step 181 — wraps on a phone. It was one non-wrapping row of the
            title and six buttons, which is wider than a phone and made the
            whole page scroll sideways (Kai). The title takes its own line
            below `lg`; the actions wrap under it. One row from `lg` up. */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-2 border-b px-4 py-3">
          {onBack ? (
            <Button variant="ghost" size="icon" onClick={onBack} aria-label="Back">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(listPathForType(t.type))}
              aria-label={`Back to ${listLabelForType(t.type)}`}
              title={`Back to ${listLabelForType(t.type)}`}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <span className="text-xs tabular-nums text-muted-foreground">{ticketRef(t.type, t.number)}</span>
          {/* Step 205 — the subject keeps the line, the actions take their own.
              Kai, on `#INC-17035 [Wordfence Alert…`: the title was sharing a
              row with six buttons from `lg` up, and `lg` is a VIEWPORT
              breakpoint while the column it lives in is ~290px narrower than
              the viewport because of the properties rail. So on a laptop the
              subject truncated after four words while the buttons had room to
              spare. It now always takes its own line, and wraps to two before
              it gives up — an email subject is where the useful part often
              is. `title` carries the whole thing for a hover. */}
          <h1 className="line-clamp-2 min-w-0 basis-full text-sm font-semibold" title={t.subject}>
            {t.subject}
          </h1>
          {t.status && (
            <Badge variant="outline" className={cn('border-transparent', STATUS_BADGE[t.status] ?? '')}>
              {data.statuses.find((s) => s.key === t.status)?.name ?? t.status}
            </Badge>
          )}
          {/* Every action on one wrapping line of its own, so adding a button
              can never squeeze the subject again. */}
          <div className="flex basis-full flex-wrap items-center gap-2">
            {/* Step 128 — "Pick up". The single most repeated act on a service
              desk, and ours was a two-step through the assignee picker.
              Taken straight from the reference's top bar, which offers
              "Pick up / Assign to me" beside Reply and Forward.

              It commits IMMEDIATELY rather than joining the properties draft:
              claiming a ticket is a race against your colleagues, and a claim
              that waits for you to press Update is a claim you can lose. */}
            {me && t.assigneeId !== me.user.id && (
              <Button
                variant="outline"
                size="sm"
                disabled={patch.isPending}
                onClick={() =>
                  patch.mutate({
                    assigneeId: me.user.id,
                    // The API refuses an assignee outside the ticket's group, so
                    // picking up an unassigned-group ticket sets both at once.
                    ...(currentGroup ? {} : { groupId: myFirstGroupId }),
                  })
                }
              >
                <UserRound /> Pick up
              </Button>
            )}
            {/* Step 55 — everything the properties rail does NOT auto-commit:
              subject, requester, department, Cc, description. Draft-first,
              because these are free-form or consequential. */}
            {/* Step 60 — collision detection. Names, not a count: "2 others" is
              a warning you cannot act on, and knowing it is the colleague
              beside you is the difference between waiting and duplicating. */}
            {alsoViewing.length > 0 && (
              <span
                className="flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs"
                title="Also viewing this ticket"
              >
                <Eye className="h-3 w-3" />
                {alsoViewing.map((p) => p.name).join(', ')}
              </span>
            )}
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil /> Edit
            </Button>
            <Button variant="outline" size="sm" onClick={() => setMergeOpen(true)}>
              Merge
            </Button>
            {/* Step 111 — the recycle bin.
              A binned ticket shows RESTORE instead, and nothing else: the
              actions that make sense on a live ticket (reply, merge, declare)
              do not apply to something in the bin, and offering them invites
              somebody to work on a ticket the requester can no longer see. */}
            {(t as { trashedAt?: number | null }).trashedAt ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => void lifecycle('restore')}
                disabled={lifecycleBusy}
              >
                <RotateCcw /> Restore
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    if (
                      !(await confirm({
                        title: `Move #${t.number} to the recycle bin?`,
                        body: 'It leaves the queue and can be restored. Anything left in the bin is destroyed automatically after 30 days.',
                        confirmLabel: 'Move to bin',
                      }))
                    )
                      return;
                    void lifecycle('trash');
                  }}
                  disabled={lifecycleBusy}
                >
                  <Trash2 /> Bin
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    if (
                      !(await confirm({
                        title: `Mark #${t.number} as spam?`,
                        body: 'It leaves the queue and is recorded as junk. You can still restore it.',
                        confirmLabel: 'Mark as spam',
                      }))
                    )
                      return;
                    void lifecycle('spam');
                  }}
                  disabled={lifecycleBusy}
                >
                  <Ban /> Spam
                </Button>
              </>
            )}
            {/* Step 54 — declaring is a TYPE CONVERSION, so it keeps the number,
              the thread and everything already written. Its own button rather
              than an option buried in the Type picker: at 03:00 nobody hunts
              through a dropdown, and the server stamps the declaration time
              from this click — the one field the review cannot reconstruct
              later. */}
            {t.type === 'incident' && (
              <Button variant="outline" size="sm" onClick={() => setDeclareOpen(true)}>
                <Megaphone /> Declare major
              </Button>
            )}
          </div>
        </div>

        {/* Step 60 — somebody else changed this while you were looking at it.
            The page has ALREADY refetched; this says who, because content
            changing silently under the cursor is the most disorienting thing
            a live page can do, and "it changed" without "who" is not
            something you can act on. Dismissible, and it never announces your
            own saves. */}
        {changedBy && (
          <div className="flex items-center gap-2 border-b border-brand/30 bg-brand/[0.07] px-4 py-1.5 text-sm">
            <RefreshCw className="h-3.5 w-3.5 shrink-0 text-brand" />
            <span className="min-w-0 flex-1">
              <span className="font-medium">{changedBy}</span> updated this ticket. You are seeing the latest.
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={dismissChange}
              aria-label="Dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}

        {/* Above the tabs, unmissable, on every tab. A major incident that
            reads like an ordinary ticket is one somebody works at ordinary
            speed. */}
        {t.type === 'major_incident' && (
          <div className="flex items-center gap-2 border-b border-destructive/30 bg-destructive/[0.07] px-4 py-2 text-sm">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-destructive/15 text-destructive">
              <Megaphone className="h-3.5 w-3.5" />
            </span>
            <span className="font-medium">Major incident</span>
            {/* Kai: *"only start and end it"* — so END is here, beside the
                declaration, rather than only on a tab somebody has to find.
                Start is the Declare button on an ordinary incident. */}
            {canEditTickets && (
              <Button
                size="sm"
                variant={mi.data?.entry?.stoodDownAt ? 'outline' : 'default'}
                disabled={standDown.isPending}
                onClick={() => standDown.mutate(mi.data?.entry?.stoodDownAt ? null : Date.now())}
              >
                {mi.data?.entry?.stoodDownAt ? 'Reopen' : 'Stand down'}
              </Button>
            )}
            {mi.data?.entry?.stoodDownAt && (
              <span className="text-xs text-muted-foreground">
                Service back {fullTime(mi.data.entry.stoodDownAt)}
              </span>
            )}
            <Link
              to={ticketPath(t.type, id, 'outage')}
              className="rounded text-xs text-brand underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Outage record
            </Link>
            <Link
              to={ticketPath(t.type, id, 'review')}
              className="rounded text-xs text-brand underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Review
            </Link>
          </div>
        )}

        {/* Step 45 — the tabs. The rail beside them stays mounted: it is
            the ticket's identity, not one tab's content, and re-rendering
            it per tab would put status and assignee out of reach from
            three of the four. */}
        {/* The count is what the payload ALREADY carries — links plus a
            parent. Children are not counted: fetching them just to put a
            number on a tab would defeat the reason they are a separate read. */}
        <TicketTabs
          id={id}
          type={t.type}
          counts={{ related: (data.links?.length ?? 0) + (data.parent ? 1 : 0) }}
        />

        {/* Step 49 — the lifecycle, on EVERY tab of a change rather than
            tucked inside the plan. Where a change is in its pipeline is the
            first thing anybody wants to know about it, and hiding that one
            tab deep would make people click to find out. */}
        {t.type === 'change' && (
          <ChangeStepper
            id={id}
            status={t.status}
            plan={data.change ?? null}
            approval={data.changeApproval ?? 'none'}
            mandatoryBoard={data.changeMandatoryBoard ?? null}
            canEdit={canEditTickets}
          />
        )}

        {tab === undefined && (
          <>
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
              {/* Description — a distinct card, not part of the scrollable thread. */}
              {/* Description.
                For a CATALOGUE request this shows the ITEM's own description,
                not message zero. Message zero for those is generated at submit
                — "Requested from the service catalogue: X" plus every answer as
                a bullet — so rendering it here printed the whole form twice on
                one screen, once as bullets and again as labelled fields in
                Request Details below (Kai, 2026-09-06: "this information is
                duplicated into the description, and the item").
                The generated body is still the right thing to SEND (the
                requester's email needs the answers in it) — it is only wrong to
                show it beside the structured version. The reference does the
                same: the item card carries the item's blurb, and the answers
                appear once.
                Everything else — incidents, email, imported tickets — keeps
                message zero, which is the requester's actual words. */}
              {(fromCatalogue ? data.catalogueItem?.description : description) && (
                <div className="rounded-lg border border-brand/30 bg-brand/5 p-4">
                  <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-brand">
                    Description
                  </p>
                  {fromCatalogue ? (
                    <p className="whitespace-pre-line text-sm">{data.catalogueItem?.description}</p>
                  ) : (
                    <div
                      className="itsm-authored prose prose-sm dark:prose-invert max-w-none overflow-x-auto"
                      dangerouslySetInnerHTML={{ __html: description!.bodyHtml }}
                    />
                  )}
                  {description && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      {description.author?.name ?? description.author?.email ?? 'Requester'} ·{' '}
                      {fullTime(description.createdAt)}
                    </p>
                  )}
                </div>
              )}

              {/* Step 62 — the request's answers, labelled and editable. */}
              {data.catalogueItem && (
                <RequestDetailsCard
                  ticketId={data.ticket.id}
                  catalogueItem={data.catalogueItem}
                  requestFields={data.requestFields ?? []}
                  values={(data.ticket.fields as Record<string, unknown>) ?? {}}
                />
              )}

              {/*
                Step 73 — the ticket's files, on the page rather than only
                inside the Edit modal.

                They were reachable but hidden: an agent had to open Edit to
                discover a ticket even had an attachment, which is exactly
                backwards for the screenshot that explains the problem. Add
                and remove stay here too — attaching a file is not an edit to
                the ticket's text, and burying it behind a draft-first modal
                made the common case the slow one.
              */}
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Attachments
                </p>
                <TicketAttachments ticketId={data.ticket.id} canEdit />
              </div>

              {conversation.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Conversations
                  </p>
                  <div className="space-y-3">
                    {conversation.map((m) =>
                      // System events (hold/release, etc.) are a log line, not a
                      // message — a bordered card for "Put on hold" reads as if
                      // someone wrote it. One compact row keeps them scannable
                      // without competing with actual replies and notes.
                      m.kind === 'system' ? (
                        <div
                          key={m.id}
                          className="flex items-center gap-2 py-0.5 text-xs text-muted-foreground"
                        >
                          <span className="h-1 w-1 shrink-0 rounded-full bg-border" />
                          <span className="min-w-0 flex-1 truncate">{m.bodyText}</span>
                          <span className="shrink-0" title={fullTime(m.createdAt)}>
                            {relativeTime(m.createdAt)}
                          </span>
                        </div>
                      ) : (
                        <div
                          key={m.id}
                          className={cn(
                            'rounded-lg border p-3',
                            m.kind === 'private_note' ? 'border-amber-500/40 bg-amber-500/10' : 'bg-card',
                          )}
                        >
                          <div className="mb-1.5 flex items-center gap-2 text-xs text-muted-foreground">
                            <span className="font-medium text-foreground">
                              {m.author?.name ?? m.author?.email ?? 'System'}
                            </span>
                            {m.kind === 'private_note' && (
                              <span className="flex items-center gap-1 font-medium text-amber-600 dark:text-amber-400">
                                <Lock className="h-3 w-3" /> Private note
                              </span>
                            )}
                            {m.cc && m.cc.length > 0 && <span>· Cc {m.cc.join(', ')}</span>}
                            {m.bcc && m.bcc.length > 0 && <span>· Bcc {m.bcc.join(', ')}</span>}
                            {m.mentions.length > 0 && (
                              <span>
                                · Notified to: {m.mentions.map((p) => p.name ?? p.email).join(', ')}
                              </span>
                            )}
                            <span title={fullTime(m.createdAt)}>{relativeTime(m.createdAt)}</span>
                          </div>
                          {/* Step 55 — editable in place, with the previous
                              wording kept. The pencil only appears where the
                              server would allow the edit: both sides read the
                              same `messageEditError`. */}
                          <MessageBody
                            ticketId={id}
                            message={m}
                            actor={{
                              id: me?.user.id ?? '',
                              isTicketsAdmin: !!me?.permissions?.tickets?.admin,
                            }}
                          />
                          {/* Step 112 — the files said WITH this message, under
                              it rather than pooled at the top, so the thread
                              reads back the way it happened. */}
                          <MessageAttachments files={(m as { attachments?: MessageFile[] }).attachments} />
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* The composer, DOCKED (Kai: *"the whole reply email bit should be
                docked as well"*).
                `shrink-0` so the thread — not the composer — gives up the
                space, and its own max height so a reply with a Cc list, a Bcc
                list and six paragraphs scrolls INSIDE the box instead of
                pushing the conversation off the top of the screen. */}
            {!composerOpen ? (
              /**
               * Step 177 — closed until somebody chooses to write.
               *
               * Two buttons and nothing else. The thread gets the space back,
               * which on a long ticket is most of the screen, and nothing is
               * drafted or autosaved until there is something to draft.
               */
              <div className="flex shrink-0 items-center gap-2 border-t bg-card p-3">
                <Button size="sm" onClick={() => openComposer('public_reply')}>
                  <Reply className="h-4 w-4" />
                  Reply
                </Button>
                <Button size="sm" variant="outline" onClick={() => openComposer('private_note')}>
                  <Lock className="h-4 w-4" />
                  Private note
                </Button>
                <span className="text-xs text-muted-foreground">
                  Replies go to the requester. Notes stay on the desk.
                </span>
              </div>
            ) : (
              <div
                className={cn(
                  'max-h-[55vh] shrink-0 overflow-y-auto border-t bg-card p-3',
                  isNote && 'bg-amber-500/5',
                )}
              >
                {restoredAt && (
                  <div className="mb-2 flex items-center gap-2 rounded-md border border-brand/30 bg-brand/5 px-2.5 py-1.5 text-xs">
                    <span>Restored your unsent draft from {fullTime(restoredAt)}.</span>
                    {/* Step 179 — the decision belongs HERE, at the moment you
                      read what it is. Being told old writing was rescued and
                      offered only a dismiss is being told about a thing you
                      cannot act on. */}
                    <button
                      onClick={() => void discardDraft()}
                      className="ml-auto shrink-0 font-medium text-muted-foreground underline-offset-2 hover:text-destructive hover:underline"
                    >
                      Discard it
                    </button>
                    <button
                      onClick={() => setRestoredAt(null)}
                      className="shrink-0 text-muted-foreground hover:text-foreground"
                      aria-label="Dismiss"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
                {draftSuspended && (
                  <p className="mb-2 text-xs text-muted-foreground">
                    This draft is open elsewhere too, so autosave paused here — your text is still safe until
                    you send or navigate away.
                  </p>
                )}
                <div className="mb-2 flex items-center gap-1">
                  <button
                    onClick={() => openComposer('public_reply')}
                    className={cn(
                      'rounded-md px-2.5 py-1 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      !isNote
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent',
                    )}
                  >
                    Reply
                  </button>
                  <button
                    onClick={() => openComposer('private_note')}
                    className={cn(
                      'rounded-md px-2.5 py-1 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      isNote ? 'bg-amber-500 text-white' : 'text-muted-foreground hover:bg-accent',
                    )}
                  >
                    Private note
                  </button>
                  {saveIndicator !== 'idle' && (
                    <span className="ml-1 text-[11px] text-muted-foreground">
                      {saveIndicator === 'saving' ? 'Saving…' : 'Saved'}
                    </span>
                  )}
                  <div className="flex-1" />
                  {/* Two different acts, and conflating them is why there was no
                    way out: Discard destroys the draft, Close keeps it. A close
                    that quietly discarded work would be the worst button on the
                    page — so it stays the safe one, and the destructive one
                    says what it does. */}
                  <button
                    onClick={() => void discardDraft()}
                    className="rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    title="Delete this draft"
                  >
                    Discard
                  </button>
                  <button
                    onClick={() => setComposerOpen(false)}
                    className="rounded-md px-1.5 py-1 text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label="Close the composer"
                    title="Close"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>

                {!isNote && (
                  <div className="mb-2 space-y-1.5 rounded-md border bg-muted/20 px-3 py-2 text-xs">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="h-3.5 w-3.5 shrink-0" />
                      <span className="shrink-0 font-medium">From:</span>
                      <span className="truncate">
                        {branding?.name ?? 'Support'} Helpdesk &lt;support@
                        {(branding?.name ?? 'support').toLowerCase().replace(/\s+/g, '')}.example&gt;
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <span className="shrink-0 font-medium">Reply To:</span>
                      <span className="truncate text-foreground">{t.requester?.email}</span>
                      <span className="ml-auto flex shrink-0 gap-2">
                        {!ccOpen && (
                          <button onClick={() => setCcOpen(true)} className="text-brand hover:underline">
                            Cc
                          </button>
                        )}
                        {!bccOpen && (
                          <button onClick={() => setBccOpen(true)} className="text-brand hover:underline">
                            Bcc
                          </button>
                        )}
                      </span>
                    </div>
                    {ccOpen && (
                      <div className="flex items-start gap-2">
                        <span className="mt-1.5 shrink-0 font-medium text-muted-foreground">Cc:</span>
                        <div className="flex-1">
                          <EmailChipInput value={cc} onChange={setCc} />
                        </div>
                      </div>
                    )}
                    {bccOpen && (
                      <div className="flex items-start gap-2">
                        <span className="mt-1.5 shrink-0 font-medium text-muted-foreground">Bcc:</span>
                        <div className="flex-1">
                          <EmailChipInput value={bcc} onChange={setBcc} />
                        </div>
                        {/* Hidden from the requester's copy only — visible to every
                        agent here, so nobody on the team is kept in the dark
                        about who else quietly saw a reply (Kai's correction). */}
                        <p className="mt-1.5 shrink-0 text-[11px] text-muted-foreground">
                          hidden from requester
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {isNote && (
                  <div className="mb-2 flex items-start gap-2 rounded-md border bg-muted/20 px-3 py-2 text-xs">
                    <span className="mt-1.5 shrink-0 font-medium text-muted-foreground">Notify to:</span>
                    <div className="flex-1">
                      <AgentChipInput
                        value={notifyTo}
                        onChange={setNotifyTo}
                        {...(currentGroup ? { candidates: agents } : {})}
                      />
                    </div>
                  </div>
                )}

                <RichTextEditor
                  value={draft}
                  onChange={setDraft}
                  caretToken={caretToken}
                  placeholder={
                    isNote
                      ? 'Visible to your team only…'
                      : `Reply to ${t.requester?.name ?? t.requester?.email}…`
                  }
                  tinted={isNote}
                />
                <div className="mt-2 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <p className="text-xs text-muted-foreground">
                      {isNote ? 'The requester will never see this.' : 'Sent to the requester.'}
                    </p>
                    <CannedResponsePicker
                      onInsert={(html) =>
                        setDraft(
                          (prev) =>
                            prev +
                            substitutePlaceholders(html, {
                              ticketRef: ticketRef(t.type, t.number),
                              ticketSubject: t.subject,
                              requesterName: t.requester?.name ?? t.requester?.email ?? '',
                              agentName: me?.user.name ?? me?.user.email ?? '',
                            }),
                        )
                      }
                    />
                  </div>
                  <Button
                    size="sm"
                    onClick={() => sendMessage.mutate()}
                    disabled={!draft.replace(/<[^>]+>/g, '').trim() || sendMessage.isPending}
                    className={cn(isNote && 'bg-amber-500 text-white hover:bg-amber-500/90')}
                  >
                    <Send /> {isNote ? 'Add note' : 'Send reply'}
                  </Button>
                </div>
                {/* Step 154 — the same words onto everything downstream.
                  Shown only when there IS something downstream: a permanent
                  disabled checkbox on every ordinary incident is furniture
                  people stop reading, and this is the one control on the
                  composer that must still be read on the day it matters. */}
                {downstreamCount > 0 && (
                  <label
                    className={cn(
                      'mt-2 flex items-start gap-2 rounded-lg border p-2.5 text-sm',
                      // Amber is the house signal for "this leaves the team", and
                      // a PUBLIC reply going to every linked requester is the
                      // single most consequential thing this composer can do.
                      propagate && !isNote && 'border-amber-500/40 bg-amber-500/10',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={propagate}
                      onChange={(e) => setPropagate(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-input"
                    />
                    <span>
                      <span className="font-medium">{propagationLabel(mode, downstreamCount)}</span>
                      <span className="block text-xs text-muted-foreground">
                        {isNote
                          ? 'Added as a private note on each. Requesters never see it.'
                          : `Emailed to ${downstreamCount === 1 ? 'that requester' : 'all those requesters'}, each under their own ticket reference.`}
                      </span>
                    </span>
                  </label>
                )}

                {/* Step 112 — attach to the reply itself. Separate from the
                  ticket's file drawer above: this rides the message, renders
                  under it in the thread, and goes out on its email. */}
                <div className="mt-2">
                  <FileFieldInput
                    value={composerFiles}
                    onChange={setComposerFiles}
                    maxFiles={10}
                    maxSizeMb={15}
                  />
                </div>
                {sendMessage.data && !sendMessage.data.ok && (
                  <p className="mt-1 text-xs text-destructive">{sendMessage.data.error}</p>
                )}
              </div>
            )}
          </>
        )}

        {/* Every non-thread tab scrolls in ONE container.
            The thread branch above has always had its own; the tabs rendered
            straight into the column, so anything taller than the viewport
            simply ran off the bottom with no way to reach it (Kai, on the
            review). `min-h-0` is what makes `flex-1` able to shrink — the
            same lesson AppShell's <main> carries. */}
        {tab !== undefined && (
          /**
           * Most tabs scroll as one block. The two that dock a Save footer
           * (Kai: *"the update button should be to the bottom of the screen
           * like in the properties"*) own their own scrolling instead, the
           * same shape as the rail beside them: a flex column whose BODY
           * scrolls, so the footer sits on the viewport edge rather than at
           * the end of the content — which on a short review left it floating
           * mid-panel.
           */
          <div
            className={cn(
              'min-h-0 flex-1',
              tab === 'review' || tab === 'outage' ? 'flex flex-col' : 'overflow-y-auto',
            )}
          >
            {/* One tab, both kinds of connection. The typed associations come
            FIRST on a problem or change, because on those the ITIL relation is
            the point and the hierarchy is incidental. */}
            {tab === 'related' && t.type !== 'task' && (
              <ConnectionsTab
                id={id}
                type={t.type}
                parent={data.parent ?? null}
                links={(data.links ?? []).map((l) => ({
                  id: l.id,
                  number: l.number,
                  type: l.type,
                  subject: l.subject,
                }))}
                canEdit={canEditTickets}
              />
            )}
            {tab === 'tasks' && t.type !== 'task' && (
              <TasksTab
                id={id}
                requesterEmail={t.requester?.email ?? me!.user.email}
                statuses={data.statuses}
                canEdit={canEditTickets}
              />
            )}
            {tab === 'problem' && t.type === 'problem' && <ProblemTab id={id} canEdit={canEditTickets} />}
            {tab === 'change' && t.type === 'change' && (
              <ChangeTab
                id={id}
                plannedStartAt={t.plannedStartAt ?? null}
                plannedEndAt={t.plannedEndAt ?? null}
                canEdit={canEditTickets}
              />
            )}
            {tab === 'outage' && t.type === 'major_incident' && (
              <OutageTab id={id} canEdit={canEditTickets} />
            )}
            {tab === 'review' && t.type === 'major_incident' && (
              <ReviewTab id={id} canEdit={canEditTickets} />
            )}
            {tab === 'approvals' && t.type !== 'task' && <ApprovalsTab id={id} canEdit={canEditTickets} />}
            {tab === 'activity' && <ActivityTab id={id} />}
            {tab === 'resolution' && t.type !== 'task' && t.type !== 'change' && (
              <ResolutionTab id={id} ticketType={t.type} canEdit={canEditTickets} />
            )}
            {/* A hand-typed URL must not show a task a tab it does not have.
            Falls back to the thread rather than a blank pane. */}
            {(t.type === 'task' || (t.type === 'change' && tab === 'resolution')) &&
              (tab === 'related' || tab === 'tasks' || tab === 'resolution' || tab === 'approvals') && (
                <div className="p-10 text-center text-sm text-muted-foreground">
                  Tasks don't have that section.{' '}
                  <a className="text-brand underline" href={`/tickets/${id}`}>
                    Back to the task
                  </a>
                  .
                </div>
              )}
          </div>
        )}
      </div>

      {/* Properties rail — auto-commit (the approved deviation). */}
      {/* The rail is a FLEX COLUMN, not a scrolling box: the body scrolls and
          the Update footer is docked to the bottom of the VIEWPORT, staying put
          however far down the properties you are. `sticky` inside the scroller
          was not enough — it pinned to the bottom of the scrolled content, so
          on a short ticket it floated mid-panel. */}
      <aside className="flex w-full shrink-0 flex-col border-t lg:w-72 lg:border-l lg:border-t-0">
        <div className="space-y-3 p-4 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
          {data.activeHold && <HeldCard activeHold={data.activeHold} />}
          {/* Step 31 — its own card, first, like the reference: the promise is
              what an agent needs BEFORE deciding what to change. */}
          {data.slaClocks?.length > 0 && (
            <section className="rounded-lg border bg-card p-3">
              <SlaPanel clocks={data.slaClocks} />
            </section>
          )}

          {/* Requester information — a card of its own in the reference, and
              it belongs above Properties: who asked shapes what you do. */}
          <section className="rounded-lg border bg-card p-3">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              <UserRound className="h-3.5 w-3.5" /> Requester
            </p>
            {t.requester ? (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand/15 text-xs font-medium text-brand">
                    {(t.requester.name ?? t.requester.email).slice(0, 1).toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    {/* Step 53 — the name IS the way through to their record.
                        "Have they asked this before?" is the first question
                        anybody has, and it used to need a queue search. */}
                    <Link
                      to={`/people/${t.requester.id}`}
                      className="block truncate rounded text-sm font-medium underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {t.requester.name ?? t.requester.email}
                    </Link>
                    {t.requester.jobTitle && (
                      <p className="truncate text-xs text-muted-foreground">{t.requester.jobTitle}</p>
                    )}
                  </div>
                </div>
                <a
                  href={`mailto:${t.requester.email}`}
                  className="block truncate text-xs text-brand hover:underline"
                >
                  {t.requester.email}
                </a>
                {/* Step 72 — the ticket belongs to whoever it is FOR, and this
                    is the other half of that: who actually filled it in. Only
                    rendered when they differ, so an ordinary ticket does not
                    carry the same name twice. */}
                {data.ticket.submitter && (
                  <p className="text-xs text-muted-foreground">
                    Raised by{' '}
                    <Link
                      to={`/people/${data.ticket.submitter.id}`}
                      className="rounded underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {data.ticket.submitter.name ?? data.ticket.submitter.email}
                    </Link>
                  </p>
                )}
                {(t.requester.department || t.requester.officeLocation) && (
                  <p className="text-xs text-muted-foreground">
                    {[t.requester.department, t.requester.officeLocation].filter(Boolean).join(' · ')}
                  </p>
                )}
                <Link
                  to={`/people/${t.requester.id}`}
                  className="block rounded text-xs text-brand underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  View more details
                </Link>
                {/* Step 43 — the ticket's Cc, editable for the life of the
                    ticket rather than set once at create. Drafted like every
                    other property: a Cc list is a multi-value edit, and
                    committing each chip as it is typed would email people
                    mid-thought. */}
                <div className="pt-1">
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    Cc <span className="font-normal">— copied on this ticket</span>
                  </label>
                  <EmailChipInput
                    value={
                      (props.cc as string[] | undefined) ?? (t as unknown as { cc: string[] | null }).cc ?? []
                    }
                    onChange={(v) => setProps((p) => ({ ...p, cc: v }))}
                  />
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Unknown</p>
            )}
          </section>

          {/* Properties — one card, one heading, fields stacked full width,
              matching the reference's "Properties" panel. */}
          <section className="space-y-3 rounded-lg border bg-card p-3">
            <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              <SlidersHorizontal className="h-3.5 w-3.5" /> Properties
            </p>
            {propertyRows}
            {/* Step 171 — sits with the properties because that is what it
                governs, and below them because it is the exception, not the
                rule. */}
            <label className="flex cursor-pointer items-start gap-2 rounded-md border border-dashed p-2 text-xs">
              <input
                type="checkbox"
                className="mt-0.5 accent-[hsl(var(--brand))]"
                checked={silent}
                onChange={(e) => setSilent(e.target.checked)}
              />
              <span>
                <span className="font-medium text-foreground">Change this quietly</span>
                <span className="block text-muted-foreground">
                  Nobody is emailed about changes you make while this is ticked — useful for a duplicate or
                  something raised by mistake. It still appears in the activity log, and it clears when you
                  leave this ticket.
                </span>
              </span>
            </label>
            {patch.data && !patch.data.ok && <p className="text-xs text-destructive">{patch.data.error}</p>}
            {patchFields.data && !patchFields.data.ok && (
              <p className="text-xs text-destructive">{patchFields.data.error}</p>
            )}
          </section>

          {/* Step 198 — how it went, beside the properties rather than at the
              bottom of the Plan tab. The plan is what we INTEND, written once
              for approvers; the outcome is what HAPPENED, recorded after the
              window by whoever did the work. Different question, different
              moment, different person. */}
          {t.type === 'change' && (
            <ChangeOutcomeCard
              id={id}
              outcome={data.change?.reviewOutcome ?? null}
              note={data.change?.reviewNote ?? null}
              canEdit={canEditTickets}
            />
          )}

          {/* Collaborate. Rendered as a stated gap rather than omitted, so it
              reads as "coming" rather than "forgotten" (the same rule the
              settings index follows for unbuilt cards). The copy names no step
              number: an internal build order is not something a customer can
              act on, and it dates the product the moment it slips. */}
          <section className="rounded-lg border border-dashed bg-muted/20 p-3">
            <p className="mb-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              <MessagesSquare className="h-3.5 w-3.5" /> Collaborate
            </p>
            <p className="text-xs text-muted-foreground">Teams and status pages are not available yet.</p>
          </section>

          {/* Read-only stamps — outside the editable card, because nothing here
              is a property you can change, and the Update button must not look
              like it applies to them. Step 149 moved Source UP into the
              editable card (it is a property the layout owns), so this card is
              now stamps only. */}
          <section className="space-y-2 rounded-lg border bg-card p-3">
            <div className="border-t pt-2">
              <VRow label="Created" value={fullTime(t.createdAt)} />
              <VRow label="Updated" value={fullTime(t.updatedAt)} />
              <VRow label="First reply" value={t.firstRespondedAt ? fullTime(t.firstRespondedAt) : null} />
              <VRow label="Resolved" value={t.resolvedAt ? fullTime(t.resolvedAt) : null} />
              <VRow label="Reference" value={ticketRef(t.type, t.number)} />
            </div>
          </section>

          {/* Step 44 — watchers. Sits beside Linked tickets because both
              answer "who and what else is attached to this", and neither is a
              property you edit in the draft: watching is an immediate act,
              like Pick up. */}
          <section className="rounded-lg border bg-card p-3">
            <div className="mb-2 flex items-center justify-between">
              <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <Eye className="h-3.5 w-3.5" /> Watchers
              </label>
              {me && (
                <button
                  onClick={() => (iAmWatching ? unwatch.mutate(me.user.id) : watch.mutate(undefined))}
                  disabled={watch.isPending || unwatch.isPending}
                  className="text-xs text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {iAmWatching ? 'Stop watching' : 'Watch this'}
                </button>
              )}
            </div>
            {(data.watchers ?? []).length === 0 ? (
              <p className="text-xs text-muted-foreground">
                Nobody yet. Watchers are told when the ticket changes.
              </p>
            ) : (
              <div className="space-y-1">
                {(data.watchers ?? []).map((w) => (
                  <div key={w.userId} className="flex items-center gap-1.5 text-xs">
                    <span className="min-w-0 flex-1 truncate">
                      {w.name ?? w.email}
                      {/* "Who put me on this" is the first question anyone
                          asks about an unexpected email. */}
                      {w.addedBy && w.addedBy !== w.userId && (
                        <span className="text-muted-foreground"> · added by a colleague</span>
                      )}
                    </span>
                    <button
                      aria-label={`Remove ${w.name ?? w.email}`}
                      onClick={() => unwatch.mutate(w.userId)}
                      className="shrink-0 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {watchError && <p className="mt-2 text-xs text-destructive">{watchError}</p>}
            <div className="mt-2">
              {/* SEARCHABLE, by Kai's rule: the lists worth searching are the
                  ones that grow with the organisation, and the staff list is
                  exactly that — a desk with sixty agents cannot scroll a
                  native select. */}
              {/* Picking a colleague IS the act of adding them, like the
                  queue's bulk-action pill — not something you change your
                  mind about before Update, and waiting would mean missing the
                  change you added them for.
                  Auto-commit on purpose: see above. */}
              <Combobox
                value={null}
                options={allAgents
                  .filter((a) => !(data.watchers ?? []).some((w) => w.userId === a.id))
                  .map((a) => ({ value: a.id, label: a.name ?? a.email }))}
                onChange={(v) => v && watch.mutate(v)}
                placeholder="Add a watcher…"
              />
            </div>
          </section>

          {/* A task's ONE relationship, shown here rather than as a tab of its
              own. Kai: it "should only be able to have a parent ticket,
              nothing else" — and one line in the rail is the honest size of
              one optional link. */}
          {t.type === 'task' && (
            <section className="rounded-lg border bg-card p-3">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Parent ticket</label>
              {data.parent ? (
                <button
                  onClick={() => navigate(ticketPath(data.parent!.type, data.parent!.id))}
                  className="block w-full truncate rounded text-left text-xs hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="tabular-nums text-muted-foreground">
                    {ticketRef(data.parent.type, data.parent.number)}
                  </span>{' '}
                  {data.parent.subject}
                </button>
              ) : (
                <p className="text-xs text-muted-foreground">Standalone — not part of a ticket.</p>
              )}
            </section>
          )}

          {/* Linked tickets are a web; a task is a leaf. The API refuses to
              link one, so offering the control would be a lie. */}
          {t.type !== 'task' && (
            <section className="rounded-lg border bg-card p-3">
              <div className="mb-1 flex items-center justify-between">
                <label className="text-xs font-medium text-muted-foreground">Linked tickets</label>
                <button onClick={() => setLinkOpen(true)} className="text-xs text-brand hover:underline">
                  + Link
                </button>
              </div>
              {data.links.length === 0 ? (
                <p className="text-xs text-muted-foreground">None yet.</p>
              ) : (
                <div className="space-y-1">
                  {data.links.map((l) => (
                    <div key={l.id} className="flex items-center gap-1.5 text-xs">
                      <Link
                        to={ticketPath(l.type, l.id)}
                        className="min-w-0 flex-1 truncate text-brand hover:underline"
                      >
                        {ticketRef(l.type, l.number)} {l.subject}
                      </Link>
                      <button
                        aria-label="Unlink"
                        onClick={() => unlinkTicket.mutate(l.id)}
                        className="shrink-0 text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
        </div>

        {/* Step 124 — DOCKED. Kai: "the update button needs to be docked to
            the bottom of my screen on the right bit and stay in the same
            position while scrolling". It sits outside the scroller entirely,
            which is what makes that true regardless of content length.

            Always PRESENT so its position never moves, DISABLED until
            something changes — visible and plainly not pressable, which is
            the honest signal. */}
        <div className="shrink-0 border-t bg-card px-4 py-3">
          {propsDirty && (
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {changedCount} unsaved change{changedCount === 1 ? '' : 's'}
              </span>
              <button
                onClick={() => {
                  setProps({});
                  setFieldsDraft({});
                }}
                className="rounded-md px-1.5 py-0.5 text-xs text-muted-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Cancel
              </button>
            </div>
          )}
          <Button
            className="w-full"
            disabled={(!propsDirty && !fieldsDirty) || patch.isPending || patchFields.isPending}
            onClick={() => {
              // Step 149 — one Update, two drafts. Built-in properties are
              // columns (the property PATCH); custom ticket fields are JSON
              // (the fields PATCH). Each clears only on its own success, so a
              // validation error in one never throws away the other.
              if (propsDirty) {
                patch.mutate(changedProps, {
                  onSuccess: (r) => {
                    if (r.ok) setProps({});
                  },
                });
              }
              if (fieldsDirty) {
                patchFields.mutate(fieldsDraft, {
                  onSuccess: (r) => {
                    if (r.ok) setFieldsDraft({});
                  },
                });
              }
            }}
          >
            {patch.isPending || patchFields.isPending ? 'Updating…' : 'Update'}
          </Button>
        </div>
      </aside>
      <Modal
        open={resolutionPrompt}
        onClose={() => setResolutionPrompt(false)}
        title="Add a resolution before resolving"
      >
        <div className="flex max-h-[70vh] flex-col">
          <p className="border-b px-4 py-2 text-xs text-muted-foreground">
            Recording what happened is required to resolve or close. Save this and the status change you asked
            for is applied.
          </p>
          {/* The SAME component as the tab — one resolution editor, so the
              modal cannot drift from the page. */}
          <ResolutionTab
            id={id}
            ticketType={t.type}
            canEdit={canEditTickets}
            onSaved={() => {
              setResolutionPrompt(false);
              // Retry what they already asked for rather than making them
              // press Update again.
              patch.mutate(changedProps, { onSuccess: (r) => r.ok && setProps({}) });
            }}
          />
        </div>
      </Modal>
      <HoldModal ticketId={id} open={holdModalOpen} onClose={() => setHoldModalOpen(false)} />
      <EditTicketModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        ticket={{
          id,
          subject: t.subject,
          cc: (t as unknown as { cc: string[] | null }).cc ?? null,
          departmentId: (t as unknown as { departmentId: string | null }).departmentId ?? null,
          source: t.source ?? null,
          requester: t.requester ?? null,
        }}
        description={description ? { id: description.id, bodyHtml: description.bodyHtml } : null}
        canEditDescription={
          !!description &&
          canEditMessage(
            { kind: description.kind, authorId: description.authorId ?? null },
            { id: me?.user.id ?? '', isTicketsAdmin: !!me?.permissions?.tickets?.admin },
          )
        }
      />
      <DeclareMajorModal
        open={declareOpen}
        onClose={() => setDeclareOpen(false)}
        id={id}
        subject={t.subject}
      />
      <Modal open={mergeOpen} onClose={() => setMergeOpen(false)} title="Merge into…">
        <div className="p-4">
          <p className="mb-2 text-xs text-muted-foreground">
            This ticket's whole conversation moves onto the ticket you pick, and this one redirects there.
          </p>
          <TicketPicker excludeId={id} onPick={(picked) => mergeInto.mutate(picked.id)} />
        </div>
      </Modal>
      <Modal open={linkOpen} onClose={() => setLinkOpen(false)} title="Link a ticket">
        <div className="p-4">
          <TicketPicker excludeId={id} onPick={(picked) => linkTicket.mutate(picked.id)} />
        </div>
      </Modal>
    </div>
  );
}
